import { Component, inject, NgZone, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthService } from './services/auth.service';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { DialogService } from './services/dialog.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule, Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoadingService } from './services/loading.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SnackBarInstallComponent } from './snack-bar-install/snack-bar-install.component';
import { Role } from './enum/Role';
import { PushNotificationService } from './services/push-notification.service';
import { SnackBarService } from './services/snack-bar.service';
import { filter } from 'rxjs';
import { MaintenanceService, MaintenanceStatus } from './services/maintenance.service';
import { NewsService } from './services/news.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule, MatProgressSpinnerModule, RouterLink, RouterLinkActive, MatToolbarModule, MatIconModule, MatButtonModule, MatSidenavModule, MatListModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {

  readonly snackBar = inject(MatSnackBar)
  public authService = inject(AuthService)
  private dialogService = inject(DialogService)
  private loadingService = inject(LoadingService);
  private breakpointObserver = inject(BreakpointObserver);
  private pushNotificationService = inject(PushNotificationService);
  private snackBarService = inject(SnackBarService);
  private maintenanceService = inject(MaintenanceService);
  private newsService = inject(NewsService);
  private router = inject(Router);
  private zone = inject(NgZone);
  readonly location = inject(Location)
  readonly isLoading$ = this.loadingService.loading$;

  isMobile = false;
  unreadNewsCount = 0;

  deferredPrompt: any;
  showInstallBanner = false;
  showNotificationPrompt = false;
  enablingNotifications = false;
  maintenanceStatus: MaintenanceStatus = {
    enabled: false,
    allowed: true,
    message: ''
  };
  notificationPromptTitle = 'Attiva le notifiche';
  notificationPromptMessage = 'Ricevi promemoria, avvisi sugli slot liberati e aggiornamenti importanti anche quando Ringo non è aperto.';
  notificationPromptCanEnable = true;
  
  title = 'Ringo';

  Role = Role;
  private readonly notificationPromptStorageKey = 'ringoNotificationPromptShown';
  private readonly notificationPromptDismissedAtStorageKey = 'ringoNotificationPromptDismissedAt';
  private readonly notificationPromptCooldownMs = 30 * 24 * 60 * 60 * 1000;
  private notificationStartupChecked = false;
  private readonly authenticatedHandler = () => void this.handleAuthenticated();
  private readonly visibilityHandler = () => {
    if (document.visibilityState === 'visible' && !this.router.url.startsWith('/novita')) {
      void this.refreshNewsIndicator();
    }
  };

  constructor() {
    if (typeof window !== 'undefined') {
      this.isMobile = this.breakpointObserver.isMatched([
        Breakpoints.XSmall,
        Breakpoints.Small
      ]);
    }
  }

  ngOnInit() {
    this.breakpointObserver.observe([
      Breakpoints.XSmall,
      Breakpoints.Small
    ]).subscribe(result => {
      this.isMobile = result.matches;
    });

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        if (event.urlAfterRedirects.startsWith('/novita')) {
          this.newsService.dismissUnreadIndicator();
        } else if (this.authService.isAuthenticated()) {
          void this.refreshNewsIndicator();
        }
        void this.refreshMaintenanceStatus();
        void this.preparePushPromptOnAppOpen();
      });

    this.newsService.unreadNewsCount$.subscribe(unreadNewsCount => {
      this.unreadNewsCount = unreadNewsCount;
    });

    this.maintenanceService.status$.subscribe(status => {
      this.maintenanceStatus = status;
      if (this.maintenanceDenied) {
        this.showNotificationPrompt = false;
      }
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('ringo-authenticated', this.authenticatedHandler);
      document.addEventListener('visibilitychange', this.visibilityHandler);

      window.addEventListener('beforeinstallprompt', (e: any) => {
        e.preventDefault();
        this.deferredPrompt = e;

        this.snackBar.openFromComponent(SnackBarInstallComponent, {
          data: {
            onInstall: () => this.installPwa()
          },
          duration: 10000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: 'custom-install-snackbar'
        });
      });
    }

    void this.initData();
  }

  async initData() {
    await this.refreshMaintenanceStatus();

    if (typeof window !== 'undefined' && localStorage.getItem('token')) {
      if (!this.maintenanceDenied) {
        await this.authService.saveUser();
        await this.refreshMaintenanceStatus();
        await this.refreshNewsIndicator();
        await this.preparePushPromptOnAppOpen();
      }
    }
  }

  get maintenanceDenied(): boolean {
    return this.maintenanceStatus.enabled && !this.maintenanceStatus.allowed;
  }

  get maintenanceBlocksApp(): boolean {
    return this.maintenanceDenied && !this.isMaintenanceLoginRoute;
  }

  get maintenanceBypassActive(): boolean {
    return this.maintenanceStatus.enabled && this.maintenanceStatus.allowed && this.authService.isAuthenticated();
  }

  get isLoginRoute(): boolean {
    return this.router.url.startsWith('/login');
  }

  get newsMenuAriaLabel(): string {
    if (this.unreadNewsCount === 1) {
      return 'Novità, 1 nuova pubblicazione';
    }

    return this.unreadNewsCount > 1
      ? `Novità, ${this.unreadNewsCount} nuove pubblicazioni`
      : 'Novità';
  }

  get unreadNewsBadgeLabel(): string {
    return this.unreadNewsCount > 9 ? '9+' : this.unreadNewsCount.toString();
  }

  get isMaintenanceLoginRoute(): boolean {
    const tree = this.router.parseUrl(this.router.url);
    return tree.root.children['primary']?.segments[0]?.path === 'login'
      && tree.queryParams['maintenanceLogin'] === '1';
  }

  private async preparePushPromptOnAppOpen(options: { forcePrompt?: boolean } = {}) {
    if (this.notificationStartupChecked && !options.forcePrompt || typeof window === 'undefined') {
      return;
    }

    if (this.maintenanceDenied) {
      return;
    }

    if (!localStorage.getItem('token')) {
      this.notificationStartupChecked = false;
      return;
    }

    if (this.pushNotificationService.wasExplicitlyDisabled()) {
      this.notificationStartupChecked = true;
      return;
    }

    if (options.forcePrompt) {
      sessionStorage.removeItem(this.notificationPromptStorageKey);
    }

    this.notificationStartupChecked = true;

    if (!this.pushNotificationService.isSupported()) {
      this.prepareUnsupportedNotificationPrompt();
      return;
    }

    const permission = this.pushNotificationService.getPermission();
    if (permission === 'granted') {
      const hasActiveSubscription = await this.pushNotificationService
        .hasActiveSubscription()
        .catch(() => false);

      if (hasActiveSubscription) {
        await this.refreshGrantedNotifications();
      } else if (!this.wasNotificationPromptRecentlyDismissed()) {
        this.openNotificationPrompt(
          'Attiva le notifiche',
          'Il browser consente le notifiche, ma questo account non è collegato al dispositivo. Premi Attiva per riceverle.',
          true
        );
      }
      return;
    }

    if (this.wasNotificationPromptRecentlyDismissed()) {
      return;
    }

    if (permission === 'denied') {
      this.openNotificationPrompt(
        'Notifiche bloccate',
        'Le notifiche sono bloccate dalle impostazioni di Android o del browser. Per riceverle devi consentirle dalle impostazioni del sito Ringo.',
        false
      );
      return;
    }

    if (permission !== 'default') {
      return;
    }

    this.openNotificationPrompt(
      'Attiva le notifiche',
      'Ricevi promemoria, avvisi sugli slot liberati e aggiornamenti importanti anche quando Ringo non è aperto.',
      true
    );
  }

  installPwa(): void {
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();
      this.deferredPrompt.userChoice.then((choiceResult: any) => {
        this.deferredPrompt = null;
      });
    }
  }

  closeBanner(): void {
    this.showInstallBanner = false;
  }

  async enableNotificationsFromPrompt() {
    this.enablingNotifications = true;

    try {
      const message = await this.pushNotificationService.subscribeToNotifications();
      await this.pushNotificationService.showEnabledNotification();
      localStorage.removeItem(this.notificationPromptDismissedAtStorageKey);
      this.showNotificationPrompt = false;
      this.snackBarService.snackSuccess(message);
    } catch (error: any) {
      this.snackBarService.snackError(error?.message || 'Impossibile attivare le notifiche');
    } finally {
      this.enablingNotifications = false;
    }
  }

  closeNotificationPrompt(): void {
    this.showNotificationPrompt = false;
  }

  dismissNotificationPrompt(): void {
    localStorage.setItem(this.notificationPromptDismissedAtStorageKey, Date.now().toString());
    this.showNotificationPrompt = false;
  }

  async logoutFromMaintenance(): Promise<void> {
    await this.authService.logout();
    void this.refreshMaintenanceStatus();
  }

  goBack(): void {
    this.location.back();
  }

  private async handleAuthenticated(): Promise<void> {
    await this.refreshMaintenanceStatus();
    await this.refreshNewsIndicator();

    if (!this.maintenanceDenied) {
      await this.preparePushPromptOnAppOpen({ forcePrompt: true });
    }
  }

  private async refreshNewsIndicator(): Promise<void> {
    const userId = this.authService.user?.id;
    if (!userId) {
      this.newsService.dismissUnreadIndicator();
      return;
    }

    await this.newsService.refreshUnreadStatus(userId).catch(() => undefined);
  }

  private async refreshMaintenanceStatus(): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    await this.maintenanceService.loadStatus();
  }

  private async refreshGrantedNotifications(): Promise<void> {
    try {
      await this.pushNotificationService.refreshSubscriptionOnServer();
    } catch {
      if (this.wasNotificationPromptRecentlyDismissed()) {
        return;
      }

      this.openNotificationPrompt(
        'Riattiva le notifiche',
        'Il permesso è già concesso, ma questo dispositivo va collegato di nuovo per ricevere le notifiche.',
        true
      );
    }
  }

  private prepareUnsupportedNotificationPrompt(): void {
    if (sessionStorage.getItem(this.notificationPromptStorageKey)
        || this.wasNotificationPromptRecentlyDismissed()) {
      return;
    }

    if (this.isIosDevice() && !this.isStandaloneMode()) {
      this.openNotificationPrompt(
        'Notifiche su iPhone',
        'Su iPhone le notifiche funzionano aprendo Ringo dall\'icona nella schermata Home. Aggiungi Ringo alla schermata Home, poi riaprilo e premi Attiva.',
        false
      );
      return;
    }

    if (this.isAndroidDevice()) {
      this.openNotificationPrompt(
        'Notifiche non disponibili',
        'Questo browser Android non espone le notifiche push. Apri Ringo da Chrome o dall\'app installata, con una connessione HTTPS.',
        false
      );
    }
  }

  private openNotificationPrompt(title: string, message: string, canEnable: boolean): void {
    this.notificationPromptTitle = title;
    this.notificationPromptMessage = message;
    this.notificationPromptCanEnable = canEnable;
    sessionStorage.setItem(this.notificationPromptStorageKey, 'true');
    window.setTimeout(() => {
      this.zone.run(() => {
        this.showNotificationPrompt = true;
      });
    }, 500);
  }

  private wasNotificationPromptRecentlyDismissed(): boolean {
    const storedValue = localStorage.getItem(this.notificationPromptDismissedAtStorageKey);
    if (!storedValue) {
      return false;
    }

    const dismissedAt = Number(storedValue);
    const elapsedTime = Date.now() - dismissedAt;
    if (!Number.isFinite(dismissedAt)
        || elapsedTime < 0
        || elapsedTime >= this.notificationPromptCooldownMs) {
      localStorage.removeItem(this.notificationPromptDismissedAtStorageKey);
      return false;
    }

    return true;
  }

  private isIosDevice(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent)
      || navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  }

  private isStandaloneMode(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  }

  private isAndroidDevice(): boolean {
    return /Android/i.test(navigator.userAgent);
  }

}
